import { z } from 'zod';

import * as Shapes from './shapes';

const FieldSchema = z.discriminatedUnion('component', [
  Shapes.Components.CheckBoxComponent.CheckboxComponentSchema,
  Shapes.Components.FileUploadComponent.FileUploadComponentSchema,
  Shapes.Components.TextInputComponent.TextInputComponentSchema,
  Shapes.Components.SelectComponent.SelectComponentSchema
]);
export type Field = z.infer<typeof FieldSchema>;

const FieldWithValueSchema = z.discriminatedUnion('component', [
  Shapes.Components.CheckBoxComponent.CheckBoxComponentWithValueSchema,
  Shapes.Components.FileUploadComponent.FileUploadComponentWithValueSchema,
  Shapes.Components.TextInputComponent.TextInputComponentWithValueSchema,
  Shapes.Components.SelectComponent.SelectComponentWithValueSchema
]);
export type FieldWithValue = z.infer<typeof FieldWithValueSchema>;
export type FieldBuildParam =
  | Shapes.Components.CheckBoxComponent.BuildParams
  | Shapes.Components.FileUploadComponent.BuildParams
  | Shapes.Components.TextInputComponent.BuildParams
  | Shapes.Components.SelectComponent.BuildParams;
export type FieldComponentType =
  | typeof Shapes.Components.CheckBoxComponent.ComponentLiteral.value
  | typeof Shapes.Components.FileUploadComponent.ComponentLiteral.value
  | typeof Shapes.Components.TextInputComponent.ComponentLiteral.value
  | typeof Shapes.Components.SelectComponent.ComponentLiteral.value;

type isValidStructure = (structure: unknown) => structure is Field;
export const isValidStructure: isValidStructure = (
  structure: unknown
): structure is Field => FieldSchema.safeParse(structure).success;

type GetValidationErrors = (
  structure: Record<string, unknown>
) => undefined | Shapes.Components.BaseComponent.InvalidComponentError;
export const getValidationErrors: GetValidationErrors = structure => {
  const parsedResult = FieldSchema.safeParse(structure);
  if (parsedResult.success) {
    return undefined;
  }

  return new Shapes.Components.BaseComponent.InvalidComponentError(
    'component' in structure
      ? <string>structure.component
      : 'Unknown component',
    parsedResult.error,
    structure
  );
};

type IsValidateData = (field: Field, value: unknown) => boolean;
export const isValidateData: IsValidateData = (field, value): boolean => {
  switch (field.component) {
    case Shapes.Components.CheckBoxComponent.ComponentLiteral.value:
      return Shapes.Components.CheckBoxComponent.isValidateData(field, value);
    case Shapes.Components.FileUploadComponent.ComponentLiteral.value:
      return Shapes.Components.FileUploadComponent.isValidateData(field, value);
    case Shapes.Components.TextInputComponent.ComponentLiteral.value:
      return Shapes.Components.TextInputComponent.isValidateData(field, value);
    case Shapes.Components.SelectComponent.ComponentLiteral.value:
      return Shapes.Components.SelectComponent.isValidateData(field, value);
    default:
      return false;
  }
};

type PopulateFromCaseDataField = (
  field: Field,
  inputJSON: Record<string, unknown>
) => FieldWithValue;
export const populateFromCaseDataField: PopulateFromCaseDataField = (
  field,
  inputJSON
) => {
  switch (field.component) {
    case Shapes.Components.CheckBoxComponent.ComponentLiteral.value:
      return Shapes.Components.CheckBoxComponent.populateFromCaseDataField(
        field,
        inputJSON
      );
    case Shapes.Components.FileUploadComponent.ComponentLiteral.value:
      return Shapes.Components.FileUploadComponent.populateFromCaseDataField(
        field,
        inputJSON
      );
    case Shapes.Components.TextInputComponent.ComponentLiteral.value:
      return Shapes.Components.TextInputComponent.populateFromCaseDataField(
        field,
        inputJSON
      );
    case Shapes.Components.SelectComponent.ComponentLiteral.value:
      return Shapes.Components.SelectComponent.populateFromCaseDataField(
        field,
        inputJSON
      );
    default:
      return field;
  }
};

type Build = (
  params: FieldBuildParam,
  componentType: FieldComponentType
) => Field | Shapes.Components.BaseComponent.InvalidComponentError;
export const build: Build = (params, componentType) => {
  switch (componentType) {
    case Shapes.Components.CheckBoxComponent.ComponentLiteral.value:
      return Shapes.Components.CheckBoxComponent.build(
        <Shapes.Components.CheckBoxComponent.BuildParams>params
      );
    case Shapes.Components.FileUploadComponent.ComponentLiteral.value:
      return Shapes.Components.FileUploadComponent.build(
        <Shapes.Components.FileUploadComponent.BuildParams>params
      );
    case Shapes.Components.TextInputComponent.ComponentLiteral.value:
      return Shapes.Components.TextInputComponent.build(
        <Shapes.Components.SelectComponent.BuildParams>params
      );
    case Shapes.Components.SelectComponent.ComponentLiteral.value:
      return Shapes.Components.SelectComponent.build(
        <Shapes.Components.SelectComponent.BuildParams>params
      );
    default:
      return new Shapes.Components.BaseComponent.InvalidComponentError(
        'Invalid component type',
        undefined,
        params
      );
  }
};
